import React, { useEffect, useRef, useState } from 'react';
import { ArrowDown, MousePointer2, Sparkles } from 'lucide-react';
import * as THREE from 'three';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import { useTranslation } from 'react-i18next';

type RobotId = 'sunny' | 'snow';
type RobotAction = 'bounce' | 'spin' | 'wiggle' | 'bow';
type RobotExpression = 'happy' | 'surprised' | 'wink' | 'curious';

type RobotInteraction = {
  id: RobotId;
  action: RobotAction;
  expression: RobotExpression;
  sequence: number;
};

const ROBOT_ACTIONS: RobotAction[] = ['bounce', 'spin', 'wiggle', 'bow'];
const ROBOT_EXPRESSIONS: RobotExpression[] = ['happy', 'surprised', 'wink', 'curious'];

const robotCopy: Record<RobotId, { name: string; color: string }> = {
  sunny: { name: 'WYNDME · Sunny', color: '#f6c94c' },
  snow: { name: 'WYNDME · Snow', color: '#f4f3ec' },
};

const material = (color: number, roughness = 0.78) =>
  new THREE.MeshStandardMaterial({ color, roughness, metalness: 0.04 });

const roundedGeometry = (width: number, height: number, depth: number, radius = 0.12) =>
  new RoundedBoxGeometry(width, height, depth, 4, Math.min(radius, width / 2, height / 2, depth / 2));

function addRoundedBox(
  parent: THREE.Group,
  size: [number, number, number],
  position: [number, number, number],
  sourceMaterial: THREE.Material,
  radius = 0.12,
) {
  const mesh = new THREE.Mesh(roundedGeometry(...size, radius), sourceMaterial);
  mesh.position.set(...position);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  parent.add(mesh);
  return mesh;
}

function addBox(
  parent: THREE.Group,
  size: [number, number, number],
  position: [number, number, number],
  color: number,
  rotation = 0,
) {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(...size), material(color));
  mesh.position.set(...position);
  mesh.rotation.y = rotation;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  parent.add(mesh);
  return mesh;
}

function HomeScene({
  onRobotSelect,
  speechBubbleRef,
}: {
  onRobotSelect: (id: RobotId, action: RobotAction, expression: RobotExpression) => void;
  speechBubbleRef: React.RefObject<HTMLDivElement | null>;
}) {
  const mountRef = useRef<HTMLDivElement>(null);
  const callbackRef = useRef(onRobotSelect);
  const robotRootsRef = useRef<Partial<Record<RobotId, THREE.Group>>>({});

  useEffect(() => {
    callbackRef.current = onRobotSelect;
  }, [onRobotSelect]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    let disposed = false;
    let frame = 0;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x191714);
    scene.fog = new THREE.FogExp2(0x6f6a60, 0.025);
    if (typeof window !== 'undefined') {
      (window as unknown as { __scene?: THREE.Scene; THREE?: typeof THREE }).__scene = scene;
      (window as unknown as { __scene?: THREE.Scene; THREE?: typeof THREE }).THREE = THREE;
    }

    const camera = new THREE.PerspectiveCamera(39, 1, 0.1, 50);
    camera.position.set(0.35, 2.62, 9.9);
    camera.zoom = window.innerWidth < 768 ? 1 : 2;
    if (typeof window !== 'undefined') (window as unknown as { __camera?: THREE.PerspectiveCamera }).__camera = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, window.innerWidth < 768 ? 1.35 : 1.8));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    renderer.shadowMap.enabled = window.innerWidth >= 768;
    renderer.shadowMap.type = THREE.PCFShadowMap;
    renderer.domElement.setAttribute('aria-label', '可互动的 WYNDME 家庭机器人三维场景');
    // Keep the CSS canvas locked to the hero. Pixel ratio should only increase
    // the drawing-buffer resolution, never the visible size of the scene.
    renderer.domElement.style.width = '100%';
    renderer.domElement.style.height = '100%';
    mount.appendChild(renderer.domElement);

    scene.add(new THREE.HemisphereLight(0xfff4df, 0x51463c, 1.85));
    const keyLight = new THREE.DirectionalLight(0xffe2ad, 3.4);
    keyLight.position.set(5, 8, 4);
    keyLight.castShadow = true;
    keyLight.shadow.mapSize.set(2048, 2048);
    keyLight.shadow.camera.left = -15;
    keyLight.shadow.camera.right = 15;
    keyLight.shadow.camera.top = 15;
    keyLight.shadow.camera.bottom = -5;
    scene.add(keyLight);
    const windowLight = new THREE.PointLight(0xffcf8b, 18, 10, 2);
    windowLight.position.set(4.8, 4.3, -2.2);
    scene.add(windowLight);
    const robotRim = new THREE.SpotLight(0xffd272, 32, 14, Math.PI / 5, 0.7, 1.6);
    robotRim.position.set(5.5, 6, 3.5);
    robotRim.target.position.set(window.innerWidth < 768 ? -0.65 : -1, 1.1, 0.15);
    scene.add(robotRim, robotRim.target);

    const room = new THREE.Group();
    scene.add(room);

    // Warm-toned materials for the living room.
    const floorMat = material(0x5b4431, 0.63);
    const wallMat = material(0xd8d0c3, 0.92);
    const windowGlassMat = new THREE.MeshStandardMaterial({
      color: 0xc78e61,
      roughness: 0.12,
      metalness: 0.05,
      emissive: 0x6f3d22,
      emissiveIntensity: 0.52,
    });
    const fabricMat = material(0xb5aa9b, 0.96);
    const darkFabricMat = material(0x716a62, 0.96);
    const woodDarkMat = material(0x4a3b2e, 0.7);
    const woodLightMat = material(0x8b6f53, 0.68);
    const metalMat = new THREE.MeshStandardMaterial({ color: 0x2a2a2a, roughness: 0.35, metalness: 0.6 });

    // Large room shell so no black shows behind the camera view.
    const floor = new THREE.Mesh(new THREE.BoxGeometry(30, 0.12, 20), floorMat);
    floor.position.set(0, -0.08, -2);
    floor.receiveShadow = true;
    room.add(floor);
    // Fine floor seams add scale and keep the room from feeling like a single block.
    const floorSeamMat = new THREE.MeshBasicMaterial({ color: 0x2f251e, transparent: true, opacity: 0.2 });
    for (let x = -12; x <= 12; x += 0.72) {
      const seam = new THREE.Mesh(new THREE.BoxGeometry(0.012, 0.006, 18), floorSeamMat);
      seam.position.set(x, 0.002, -1.5);
      room.add(seam);
    }
    const backWall = new THREE.Mesh(new THREE.BoxGeometry(30, 12, 0.12), wallMat);
    backWall.position.set(0, 5.92, -7);
    backWall.receiveShadow = true;
    room.add(backWall);
    const leftWall = new THREE.Mesh(new THREE.BoxGeometry(0.12, 12, 20), wallMat);
    leftWall.position.set(-12, 5.92, -2);
    leftWall.receiveShadow = true;
    room.add(leftWall);
    const rightWall = new THREE.Mesh(new THREE.BoxGeometry(0.12, 12, 20), wallMat);
    rightWall.position.set(12, 5.92, -2);
    rightWall.receiveShadow = true;
    room.add(rightWall);

    // Baseboards.
    addBox(room, [30, 0.22, 0.18], [0, 0.03, -6.88], 0xeae5dc);
    addBox(room, [0.18, 0.22, 20], [-11.88, 0.03, -2], 0xeae5dc);
    addBox(room, [0.18, 0.22, 20], [11.88, 0.03, -2], 0xeae5dc);

    // Large window on the back wall with frame, sill and curtains.
    const windowGroup = new THREE.Group();
    windowGroup.position.set(5, 4.4, -6.88);
    room.add(windowGroup);
    const windowGlass = new THREE.Mesh(new THREE.BoxGeometry(5.2, 3.6, 0.08), windowGlassMat);
    windowGlass.position.z = 0.02;
    windowGroup.add(windowGlass);
    const sun = new THREE.Mesh(
      new THREE.CircleGeometry(0.3, 40),
      new THREE.MeshBasicMaterial({ color: 0xffd696, transparent: true, opacity: 0.92 }),
    );
    sun.position.set(1.55, 0.82, 0.08);
    windowGroup.add(sun);
    addBox(windowGroup, [5.4, 0.14, 0.22], [0, 1.87, 0.1], 0xeae5dc);
    addBox(windowGroup, [5.4, 0.14, 0.22], [0, -1.87, 0.1], 0xeae5dc);
    addBox(windowGroup, [0.14, 3.8, 0.22], [-2.63, 0, 0.1], 0xeae5dc);
    addBox(windowGroup, [0.14, 3.8, 0.22], [2.63, 0, 0.1], 0xeae5dc);
    addBox(windowGroup, [0.08, 3.7, 0.08], [0, 0, 0.1], 0xeae5dc);
    addBox(windowGroup, [5.5, 0.18, 0.42], [0, -2.05, 0.22], 0x978a78);
    // Curtains.
    const curtainLeft = new THREE.Mesh(new THREE.BoxGeometry(0.9, 4.2, 0.12), material(0xe8e0d1, 0.95));
    curtainLeft.position.set(-3, 0, 0.26);
    curtainLeft.rotation.z = 0.04;
    windowGroup.add(curtainLeft);
    const curtainRight = new THREE.Mesh(new THREE.BoxGeometry(0.9, 4.2, 0.12), material(0xe8e0d1, 0.95));
    curtainRight.position.set(3, 0, 0.26);
    curtainRight.rotation.z = -0.04;
    windowGroup.add(curtainRight);

    // Large area rug.
    const rug = new THREE.Mesh(new THREE.CircleGeometry(4.8, 64), material(0xa88b67, 0.98));
    rug.scale.set(1.6, 0.8, 1);
    rug.rotation.x = -Math.PI / 2;
    rug.position.set(0.5, 0.01, -0.6);
    rug.receiveShadow = true;
    room.add(rug);
    const rugInset = new THREE.Mesh(
      new THREE.RingGeometry(3.85, 4.05, 64),
      new THREE.MeshBasicMaterial({ color: 0xe2cda9, transparent: true, opacity: 0.32, side: THREE.DoubleSide }),
    );
    rugInset.scale.set(1.6, 0.8, 1);
    rugInset.rotation.x = -Math.PI / 2;
    rugInset.position.set(0.5, 0.018, -0.6);
    room.add(rugInset);

    // L-shaped sofa.
    const sofaGroup = new THREE.Group();
    sofaGroup.position.set(-4.6, 0, -3.4);
    sofaGroup.rotation.y = 0.12;
    room.add(sofaGroup);
    addRoundedBox(sofaGroup, [3.6, 0.55, 1.4], [0, 0.28, 0], fabricMat, 0.18);
    const sofaBack = addRoundedBox(sofaGroup, [3.6, 0.9, 0.32], [0, 0.72, -0.54], fabricMat, 0.14);
    sofaBack.rotation.x = -0.08;
    addRoundedBox(sofaGroup, [0.28, 0.75, 1.4], [-1.66, 0.4, 0], fabricMat, 0.12);
    addRoundedBox(sofaGroup, [0.28, 0.75, 1.4], [1.66, 0.4, 0], fabricMat, 0.12);
    // Sofa chaise section.
    addRoundedBox(sofaGroup, [1.5, 0.42, 2.4], [2.4, 0.21, 0.9], fabricMat, 0.16);
    addRoundedBox(sofaGroup, [0.28, 0.8, 2.4], [3.16, 0.5, 0.9], fabricMat, 0.12);
    // Cushions.
    [[-1.1, 0.78, -0.35], [0, 0.78, -0.35], [1.1, 0.78, -0.35], [2.5, 0.62, 0.4], [2.5, 0.62, 1.4]].forEach(([x, y, z]) => {
      const cushion = new THREE.Mesh(roundedGeometry(0.8, 0.28, 0.28, 0.1), darkFabricMat);
      cushion.position.set(x as number, y as number, z as number);
      cushion.rotation.x = -0.12;
      cushion.castShadow = true;
      sofaGroup.add(cushion);
    });

    // Coffee table.
    const tableGroup = new THREE.Group();
    tableGroup.position.set(0.2, 0, -0.6);
    room.add(tableGroup);
    const tableTop = new THREE.Mesh(roundedGeometry(1.9, 0.1, 1.1, 0.05), woodLightMat);
    tableTop.position.set(0, 0.5, 0);
    tableTop.castShadow = true;
    tableGroup.add(tableTop);
    [[-0.78, -0.42], [0.78, -0.42], [-0.78, 0.42], [0.78, 0.42]].forEach(([x, z]) => {
      const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.03, 0.5, 12), metalMat);
      leg.position.set(x as number, 0.25, z as number);
      leg.castShadow = true;
      tableGroup.add(leg);
    });
    // A small book on the table.
    const book = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.05, 0.45), material(0x8fa8b8));
    book.position.set(0.25, 0.56, 0.1);
    book.rotation.y = 0.25;
    book.castShadow = true;
    tableGroup.add(book);
    // A mug and a gently glowing family moment make the set feel lived-in.
    const mug = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.085, 0.18, 20), material(0xe5dfd3, 0.42));
    mug.position.set(-0.42, 0.65, -0.12);
    mug.castShadow = true;
    tableGroup.add(mug);
    const steamMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.18 });
    const steam = new THREE.Mesh(new THREE.TorusGeometry(0.035, 0.007, 8, 18, Math.PI * 1.25), steamMaterial);
    steam.rotation.x = Math.PI / 2;
    steam.position.set(-0.42, 0.82, -0.12);
    tableGroup.add(steam);

    // TV console and television on the right wall.
    const tvGroup = new THREE.Group();
    tvGroup.position.set(7.2, 0, -5.5);
    tvGroup.rotation.y = -0.2;
    room.add(tvGroup);
    addBox(tvGroup, [4.2, 0.55, 0.55], [0, 0.28, 0], 0x4a3b2e);
    addBox(tvGroup, [0.08, 0.5, 0.08], [-1.8, 0.25, 0.18], 0x2a2a2a);
    addBox(tvGroup, [0.08, 0.5, 0.08], [1.8, 0.25, 0.18], 0x2a2a2a);
    const tvFrame = new THREE.Mesh(new THREE.BoxGeometry(3.35, 2.0, 0.06), material(0x1a1a1a));
    tvFrame.position.set(0, 1.55, 0.14);
    tvGroup.add(tvFrame);
    const tvScreen = new THREE.Mesh(
      new THREE.PlaneGeometry(3.15, 1.8),
      new THREE.MeshStandardMaterial({ color: 0x263238, emissive: 0x16242b, emissiveIntensity: 0.75, roughness: 0.2 }),
    );
    tvScreen.position.set(0, 1.55, 0.185);
    tvGroup.add(tvScreen);
    const tvGlow = new THREE.PointLight(0x8fd5db, 5.5, 4.5, 2);
    tvGlow.position.set(0, 1.5, 1.2);
    tvGroup.add(tvGlow);

    // Floor lamp near the sofa.
    const lampGroup = new THREE.Group();
    lampGroup.position.set(-5.8, 0, -0.6);
    room.add(lampGroup);
    const lampPole = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.05, 2.4, 12), metalMat);
    lampPole.position.y = 1.2;
    lampPole.castShadow = true;
    lampGroup.add(lampPole);
    const lampShade = new THREE.Mesh(new THREE.ConeGeometry(0.42, 0.55, 32, 1, true), material(0xf4f0e6, 0.8));
    lampShade.position.y = 2.45;
    lampGroup.add(lampShade);
    const lampLight = new THREE.PointLight(0xffecd2, 18, 7, 2);
    lampLight.position.set(0, 2.15, 0);
    lampGroup.add(lampLight);

    // Bookshelf on the left wall.
    const shelfGroup = new THREE.Group();
    shelfGroup.position.set(-10.5, 0, -1.5);
    shelfGroup.rotation.y = 0.15;
    room.add(shelfGroup);
    addBox(shelfGroup, [2.4, 0.1, 0.55], [0, 0.05, 0], 0x8b6f53);
    addBox(shelfGroup, [2.4, 0.1, 0.55], [0, 1.05, 0], 0x8b6f53);
    addBox(shelfGroup, [2.4, 0.1, 0.55], [0, 2.05, 0], 0x8b6f53);
    addBox(shelfGroup, [2.4, 0.1, 0.55], [0, 3.05, 0], 0x8b6f53);
    addBox(shelfGroup, [0.1, 3.1, 0.55], [-1.15, 1.55, 0], 0x8b6f53);
    addBox(shelfGroup, [0.1, 3.1, 0.55], [1.15, 1.55, 0], 0x8b6f53);
    // Books.
    for (let i = 0; i < 18; i += 1) {
      const h = 0.55 + Math.random() * 0.25;
      const w = 0.08 + Math.random() * 0.08;
      const bookColor = [0x8fa8b8, 0xb88f8f, 0xa5b88f, 0xc7a75b, 0x9d8567][i % 5];
      const bookMesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, 0.22), material(bookColor));
      const shelfY = i < 6 ? 1.1 : i < 12 ? 2.1 : 3.1;
      const shelfX = (i % 6) * 0.32 - 0.78;
      bookMesh.position.set(shelfX, shelfY + h / 2, 0);
      bookMesh.castShadow = true;
      shelfGroup.add(bookMesh);
    }

    // Wall art above the sofa.
    const frameGroup = new THREE.Group();
    frameGroup.position.set(-1.7, 3.65, -6.38);
    frameGroup.rotation.y = -0.05;
    room.add(frameGroup);
    const pictureFrame = new THREE.Mesh(new THREE.BoxGeometry(2.2, 1.5, 0.06), material(0x3a332c));
    frameGroup.add(pictureFrame);
    const art = new THREE.Mesh(new THREE.BoxGeometry(1.9, 1.2, 0.04), material(0xc7a75b));
    art.position.z = 0.04;
    frameGroup.add(art);
    const artSun = new THREE.Mesh(new THREE.CircleGeometry(0.28, 32), material(0xf1dca8, 0.8));
    artSun.position.set(0.42, 0.18, 0.075);
    frameGroup.add(artSun);
    const artHorizon = new THREE.Mesh(new THREE.BoxGeometry(1.35, 0.08, 0.03), material(0x756a5c, 0.82));
    artHorizon.position.set(-0.15, -0.24, 0.075);
    artHorizon.rotation.z = -0.16;
    frameGroup.add(artHorizon);

    // A trio of small family frames balances the composition around the hero robot.
    [-0.72, 0, 0.72].forEach((x, index) => {
      const familyFrame = new THREE.Group();
      familyFrame.position.set(1.9 + x, 2.55 + (index % 2) * 0.18, -6.78);
      const frame = new THREE.Mesh(roundedGeometry(0.48, 0.58, 0.05, 0.025), material(0x5a4735, 0.68));
      const photo = new THREE.Mesh(new THREE.PlaneGeometry(0.38, 0.47), material([0x8ca29d, 0xb79a84, 0x9b8fab][index], 0.9));
      photo.position.z = 0.031;
      familyFrame.add(frame, photo);
      room.add(familyFrame);
    });

    // Ceiling pendant light.
    const pendantGroup = new THREE.Group();
    pendantGroup.position.set(0.5, 7.5, -1.5);
    room.add(pendantGroup);
    const pendantCord = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 1.2, 8), metalMat);
    pendantCord.position.y = 0.6;
    pendantGroup.add(pendantCord);
    const pendantShade = new THREE.Mesh(new THREE.ConeGeometry(0.55, 0.45, 32, 1, true), material(0xf4f0e6, 0.75));
    pendantShade.position.y = -0.05;
    pendantGroup.add(pendantShade);
    const pendantLight = new THREE.PointLight(0xfff0d6, 28, 10, 2);
    pendantLight.position.y = -0.35;
    pendantLight.castShadow = true;
    pendantLight.shadow.mapSize.set(512, 512);
    pendantGroup.add(pendantLight);

    // Large potted plant near the window.
    const bigPot = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.36, 0.85, 24), material(0xa56542));
    bigPot.position.set(9, 0.42, -4.5);
    bigPot.castShadow = true;
    room.add(bigPot);
    for (let i = 0; i < 12; i += 1) {
      const leaf = new THREE.Mesh(new THREE.SphereGeometry(0.42, 16, 10), material(i % 2 ? 0x537451 : 0x365d43));
      leaf.scale.set(0.6, 1.7, 0.4);
      const angle = i * 0.9;
      leaf.position.set(
        9 + Math.sin(angle) * 0.62,
        1.15 + (i % 4) * 0.32 + Math.random() * 0.1,
        -4.5 + Math.cos(angle) * 0.42,
      );
      leaf.rotation.z = Math.sin(angle) * 0.5;
      leaf.rotation.x = Math.cos(angle) * 0.25;
      leaf.castShadow = true;
      room.add(leaf);
    }

    // Small side table with a second plant.
    const sideTable = new THREE.Group();
    sideTable.position.set(-6, 0, -4.2);
    room.add(sideTable);
    addBox(sideTable, [0.85, 0.05, 0.85], [0, 0.55, 0], 0x8b6f53);
    addBox(sideTable, [0.08, 0.55, 0.08], [0, 0.28, 0], 0x4a3b2e);
    const smallPot = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.16, 0.32, 16), material(0xa56542));
    smallPot.position.set(0, 0.74, 0);
    smallPot.castShadow = true;
    sideTable.add(smallPot);
    for (let i = 0; i < 5; i += 1) {
      const leaf = new THREE.Mesh(new THREE.SphereGeometry(0.18, 12, 8), material(i % 2 ? 0x6b8f5e : 0x4a7a3f));
      leaf.scale.set(0.5, 1.6, 0.35);
      leaf.position.set(
        Math.sin(i * 1.7) * 0.18,
        1.05 + (i % 3) * 0.12,
        Math.cos(i * 1.3) * 0.12,
      );
      leaf.rotation.z = Math.sin(i * 2.5) * 0.35;
      leaf.castShadow = true;
      sideTable.add(leaf);
    }

    // Familiar everyday objects give the robot a believable place in family life.
    const playCorner = new THREE.Group();
    playCorner.position.set(3.6, 0.02, 1.25);
    room.add(playCorner);
    const toyColors = [0xe8aa55, 0x789a91, 0xc87965];
    [[0, 0.13, 0], [0.28, 0.11, 0.08], [0.12, 0.1, -0.3]].forEach(([x, y, z], index) => {
      const block = new THREE.Mesh(roundedGeometry(0.24, 0.24, 0.24, 0.045), material(toyColors[index], 0.82));
      block.position.set(x, y, z);
      block.rotation.y = index * 0.42;
      block.castShadow = true;
      playCorner.add(block);
    });
    const ball = new THREE.Mesh(new THREE.SphereGeometry(0.19, 24, 16), material(0xe3d5b9, 0.86));
    ball.position.set(0.55, 0.2, -0.18);
    ball.castShadow = true;
    playCorner.add(ball);

    const followMarkerMaterial = new THREE.MeshBasicMaterial({
      color: 0xf0c85a,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const followMarker = new THREE.Mesh(new THREE.RingGeometry(0.15, 0.21, 48), followMarkerMaterial);
    followMarker.rotation.x = -Math.PI / 2;
    followMarker.position.set(window.innerWidth < 768 ? -0.75 : 2.2, 0.016, window.innerWidth < 768 ? 0.45 : 1.55);
    room.add(followMarker);

    const dustGeometry = new THREE.BufferGeometry();
    const dustPositions = new Float32Array(54 * 3);
    for (let i = 0; i < 54; i += 1) {
      dustPositions[i * 3] = 0.5 + Math.random() * 7;
      dustPositions[i * 3 + 1] = 0.5 + Math.random() * 5;
      dustPositions[i * 3 + 2] = -5 + Math.random() * 6;
    }
    dustGeometry.setAttribute('position', new THREE.BufferAttribute(dustPositions, 3));
    const dust = new THREE.Points(
      dustGeometry,
      new THREE.PointsMaterial({ color: 0xffe2b2, size: 0.025, transparent: true, opacity: 0.42, depthWrite: false }),
    );
    scene.add(dust);

    const robotRoots: Partial<Record<RobotId, THREE.Group>> = {};
    const robotMeshes: THREE.Object3D[] = [];
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('/draco/');
    const loader = new GLTFLoader();
    loader.setDRACOLoader(dracoLoader);

    const loadRobot = async (id: RobotId, file: string, x: number, z: number, facing: number) => {
      const gltf = await loader.loadAsync(`/3d/${file}`);
      if (disposed) return;
      const root = new THREE.Group();
      const actionPivot = new THREE.Group();
      root.add(actionPivot);
      const model = gltf.scene;
      const bounds = new THREE.Box3().setFromObject(model);
      const size = bounds.getSize(new THREE.Vector3());
      const center = bounds.getCenter(new THREE.Vector3());
      // Preserve the GLB exactly as authored: only a uniform presentation scale
      // is allowed. Product dimensions inform scene design but never reshape it.
      const targetHeight = id === 'sunny' ? 0.41 : 0.38;
      const uniformScale = targetHeight / Math.max(size.y, 0.01);
      model.scale.setScalar(uniformScale);
      model.position.set(
        -center.x * uniformScale,
        -bounds.min.y * uniformScale,
        -center.z * uniformScale,
      );
      const modelMeshes: THREE.Mesh[] = [];
      const wheelNodes: THREE.Object3D[] = [];
      model.traverse((child) => {
        const isWheel = /wheel|tire|轮/i.test(child.name);
        const parentIsWheel = child.parent ? /wheel|tire|轮/i.test(child.parent.name) : false;
        if (isWheel && !parentIsWheel) wheelNodes.push(child);
        if ((child as THREE.Mesh).isMesh) {
          const mesh = child as THREE.Mesh;
          mesh.castShadow = true;
          mesh.receiveShadow = true;
          mesh.userData.robotId = id;
          modelMeshes.push(mesh);
          robotMeshes.push(mesh);
        }
      });

      actionPivot.add(model);
      root.updateMatrixWorld(true);

      // Identify head parts by node name. GLTFLoader creates named nodes for each
      // Rhino layer; the head geometry may be split into several nodes (head, head_1,
      // head_5, head_7, etc.). Attach the *nodes* themselves so their local transforms
      // are preserved and they rotate as a single rigid group.
      const headNodes: THREE.Object3D[] = [];
      const neckNodes: THREE.Object3D[] = [];
      const eyeNodes: THREE.Object3D[] = [];

      model.traverse((node) => {
        const name = node.name.toLowerCase();
        if (name.startsWith('head') || name === 'eye_l_back' || name === 'eye_r_back') {
          headNodes.push(node);
        } else if (name === 'neck') {
          neckNodes.push(node);
        } else if (name === 'eye_l' || name === 'eye_r') {
          eyeNodes.push(node);
        }
      });

      const neckParts = neckNodes.filter((node) => (node as THREE.Mesh).isMesh) as THREE.Mesh[];
      const neckBounds = new THREE.Box3();
      neckParts.forEach((mesh) => neckBounds.expandByObject(mesh));
      const neckCenter = neckBounds.isEmpty()
        ? new THREE.Vector3(0, targetHeight * 0.68, 0)
        : neckBounds.getCenter(new THREE.Vector3());
      if (!neckBounds.isEmpty()) neckCenter.y = neckBounds.min.y;
      const headPivot = new THREE.Group();
      headPivot.position.copy(neckCenter);
      actionPivot.add(headPivot);
      root.updateMatrixWorld(true);

      // Some exports put the actual screen surfaces in a white PBR material
      // rather than under the Eye_L/Eye_R empty markers. Collect those meshes
      // and pick the front-most layer for each eye.
      const screenCandidates: THREE.Mesh[] = [];
      model.traverse((node) => {
        if ((node as THREE.Mesh).isMesh) {
          const mesh = node as THREE.Mesh;
          const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
          if (mats.some((m) => m && m.name === 'PBR 材质')) {
            screenCandidates.push(mesh);
          }
        }
      });
      const screensBySide: Record<'left' | 'right', THREE.Mesh[]> = { left: [], right: [] };
      screenCandidates.forEach((mesh) => {
        const center = new THREE.Box3().setFromObject(mesh).getCenter(new THREE.Vector3());
        // The robot faces -Z and the camera looks from +Z; the viewer's left
        // corresponds to the model's +x. Eye_L is on this side in the current export.
        const side: 'left' | 'right' = center.x > 0 ? 'left' : 'right';
        screensBySide[side].push(mesh);
      });
      const frontScreens: THREE.Mesh[] = [];
      (['left', 'right'] as const).forEach((side) => {
        const list = screensBySide[side];
        if (list.length === 0) return;
        // The camera sits on +Z and looks toward -Z, so the largest world-z value
        // is the front-most visible screen layer. Pick that layer and hide deeper
        // duplicates behind it.
        list.sort(
          (a, b) =>
            new THREE.Box3().setFromObject(b).getCenter(new THREE.Vector3()).z -
            new THREE.Box3().setFromObject(a).getCenter(new THREE.Vector3()).z,
        );
        frontScreens.push(list[0]);
        list.slice(1).forEach((m) => {
          m.visible = false;
          headPivot.attach(m);
        });
      });

      if (headNodes.length > 0) {
        headNodes.forEach((node) => headPivot.attach(node));
      } else {
        // Fallback for legacy/unnamed models: attach any mesh above the neckline.
        const fallbackHeadParts = modelMeshes.filter((mesh) => {
          const meshCenter = new THREE.Box3().setFromObject(mesh).getCenter(new THREE.Vector3());
          return meshCenter.y > 1.43;
        });
        fallbackHeadParts.forEach((mesh) => headPivot.attach(mesh));
      }

      // Set up eye screen meshes so they render correctly with the head pivot.
      // No animated emoji textures are applied; the screen keeps its emissive colour.
      const eyeMaterials: THREE.MeshStandardMaterial[] = [];
      const eyes: THREE.Object3D[] = [];
      const eyeGroups: { side: 'left' | 'right'; meshes: THREE.Mesh[] }[] = [];

      if (eyeNodes.length === 2) {
        eyeNodes.sort((a, b) => a.name.localeCompare(b.name)).forEach((eyeNode) => {
          const eyeMeshes: THREE.Mesh[] = [];
          eyeNode.traverse((child) => {
            if ((child as THREE.Mesh).isMesh) eyeMeshes.push(child as THREE.Mesh);
          });
          if (eyeMeshes.length === 0) return;
          const eyeSide: 'left' | 'right' =
            eyeNode.name.toLowerCase() === 'eye_l' ? 'left' : 'right';
          eyeGroups.push({ side: eyeSide, meshes: eyeMeshes });
        });
      }

      if (eyeGroups.length === 0 && frontScreens.length > 0) {
        frontScreens.forEach((mesh) => {
          const center = new THREE.Box3().setFromObject(mesh).getCenter(new THREE.Vector3());
          const side: 'left' | 'right' = center.x > 0 ? 'left' : 'right';
          eyeGroups.push({ side, meshes: [mesh] });
        });
      }

      if (eyeGroups.length > 0) {
        eyeGroups.forEach(({ side: eyeSide, meshes: eyeMeshes }) => {
          // Compute the actual centre of the eye meshes in head-pivot space.
          // Rhino exports the Eye_L/R group origin at the face centre while the
          // mesh vertices are offset to the real eye position; use the mesh bounds
          // so scaling/blinking rotates around the eye centre.
          root.updateMatrixWorld(true);
          const eyeWorldBox = new THREE.Box3();
          eyeMeshes.forEach((mesh) => eyeWorldBox.expandByObject(mesh));
          const eyeWorldCenter = eyeWorldBox.getCenter(new THREE.Vector3());
          const eyeLocalCenter = headPivot.worldToLocal(eyeWorldCenter.clone());

          const eyePivot = new THREE.Group();
          eyePivot.position.copy(eyeLocalCenter);
          headPivot.add(eyePivot);

          eyeMeshes.forEach((eye) => {
            // Recompute bounds and disable frustum culling. Some Rhino exports
            // ship a stale bounding box that makes Three.js cull the eye screen
            // even though it is right in front of the camera.
            eye.geometry.computeBoundingBox();
            eye.geometry.computeBoundingSphere();
            eye.frustumCulled = false;
            const sourceMaterials = Array.isArray(eye.material) ? eye.material : [eye.material];
            const clonedMaterials = sourceMaterials.map((item) => item.clone());
            eye.material = Array.isArray(eye.material) ? clonedMaterials : clonedMaterials[0];
            clonedMaterials.forEach((item) => {
              if (item instanceof THREE.MeshStandardMaterial) {
                // For the white PBR screen, use white emissive so the screen glows;
                // otherwise fall back to the robot's accent colour.
                if (item.name === 'PBR 材质' || item.color.getHex() === 0xffffff) {
                  item.emissive.setHex(0xffffff);
                } else if (item.emissive.r === 0 && item.emissive.g === 0 && item.emissive.b === 0) {
                  item.emissive.setHex(id === 'sunny' ? 0xffa51f : 0x67d8ff);
                }
                item.emissiveIntensity = 0.95;
                item.userData.baseEmissiveIntensity = 0.95;
                item.userData.eyeSide = eyeSide;
                // The Rhino-exported eye planes face +Z while the camera looks
                // from +Z toward -Z, so the visible side is the back face. Render
                // both sides so the screen is visible from the front.
                item.side = THREE.DoubleSide;
                eyeMaterials.push(item);
              }
            });
            eyePivot.attach(eye);
          });

          eyePivot.userData.robotId = id;
          eyePivot.userData.eyeSide = eyeSide;
          eyes.push(eyePivot);
        });
      } else {
        // Unnamed legacy models receive a small emissive screen layer.
        const eyeMaterial = new THREE.MeshStandardMaterial({
          color: id === 'sunny' ? 0xffde72 : 0xdff7ff,
          emissive: id === 'sunny' ? 0xffb000 : 0x67d8ff,
          emissiveIntensity: 3.2,
          roughness: 0.22,
          metalness: 0.05,
        });
        eyeMaterial.userData.baseEmissiveIntensity = 3.2;
        eyeMaterials.push(eyeMaterial);
        const eyeGeometry = new THREE.SphereGeometry(0.11, 24, 16);
        [-1, 1].forEach((side) => {
          const eye = new THREE.Mesh(eyeGeometry, eyeMaterial);
          eye.position.set(side * 0.235, 0.12, 0.79);
          eye.scale.set(1, 0.62, 0.22);
          eye.userData.robotId = id;
          eye.userData.eyeSide = side === -1 ? 'left' : 'right';
          robotMeshes.push(eye);
          headPivot.add(eye);
          eyes.push(eye);
        });
      }

      eyes.forEach((eye) => {
        eye.userData.baseScale = eye.scale.clone();
        eye.userData.baseRotationZ = eye.rotation.z;
      });

      // Keep the small, furniture-scale robot easy to hover and click.
      const hitProxy = new THREE.Mesh(
        new THREE.SphereGeometry(Math.max(targetHeight * 0.52, 0.22), 16, 12),
        new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false }),
      );
      hitProxy.position.y = Math.max(targetHeight * 0.52, 0.18);
      hitProxy.userData.robotId = id;
      robotMeshes.push(hitProxy);
      root.add(hitProxy);

      // The rug surface sits at y=0.01. Keep the wheel contact point only a
      // millimetre above it so the robot reads as grounded without z-fighting.
      root.position.set(x, 0.011, z);
      root.rotation.y = facing;
      root.userData.baseY = root.position.y;
      root.userData.targetHeight = targetHeight;
      root.userData.homeX = x;
      root.userData.homeZ = z;
      root.userData.walkTarget = new THREE.Vector3(x, 0, z);
      root.userData.velocity = new THREE.Vector3();
      root.userData.wheels = wheelNodes;
      root.userData.followMarker = followMarker;
      root.userData.followMarkerMaterial = followMarkerMaterial;
      root.userData.rimTarget = robotRim.target;
      root.userData.phase = id === 'sunny' ? 0 : Math.PI;
      root.userData.actionPivot = actionPivot;
      root.userData.headPivot = headPivot;
      root.userData.eyes = eyes;
      root.userData.eyeMaterials = eyeMaterials;
      root.userData.hovered = false;
      robotRoots[id] = root;
      robotRootsRef.current[id] = root;
      scene.add(root);
    };

    void Promise.all([
      loadRobot(
        'sunny',
        '轻量化歪觅模型_黄_new.glb',
        window.innerWidth < 768 ? -0.75 : 2.2,
        window.innerWidth < 768 ? 0.45 : 1.55,
        -0.12,
      ),
    ]).catch((error) => console.error('Robot model failed to load', error));

    const pointer = new THREE.Vector2();
    const pointerTarget = new THREE.Vector2();
    const raycaster = new THREE.Raycaster();
    const walkPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    const pointerWorld = new THREE.Vector3();
    const speechAnchor = new THREE.Vector3();
    let hovered: RobotId | null = null;
    let pointerInside = false;
    let lastTime = performance.now();
    const cameraHome = new THREE.Vector3(0.35, 2.62, 9.9);
    const cameraTarget = new THREE.Vector3(0.25, 0.48, -0.7);

    const updatePointer = (event: PointerEvent) => {
      const rect = renderer.domElement.getBoundingClientRect();
      pointerTarget.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      pointerTarget.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      pointerInside = true;
      raycaster.setFromCamera(pointerTarget, camera);
      if (raycaster.ray.intersectPlane(walkPlane, pointerWorld)) {
        (Object.values(robotRoots) as THREE.Group[]).forEach((robot) => {
          const homeX = robot.userData.homeX as number;
          const homeZ = robot.userData.homeZ as number;
          const isMobile = mount.clientWidth < 768;
          // Clamp against the room itself, not against the robot's starting point.
          // This keeps pointer-to-floor tracking useful across ultrawide viewports.
          const minX = isMobile ? -2.55 : -5.2;
          const maxX = isMobile ? 2.15 : 7.9;
          let nextX = THREE.MathUtils.clamp(pointerWorld.x, minX, maxX);
          let nextZ = THREE.MathUtils.clamp(
            pointerWorld.z,
            homeZ - (isMobile ? 1.1 : 0.75),
            homeZ + (isMobile ? 1.1 : 0.25),
          );
          // Route through a fixed-depth lane in front of the coffee table.
          if (!isMobile && nextX > -0.82) nextZ = THREE.MathUtils.clamp(nextZ, 1.2, 1.72);
          const walkTarget = robot.userData.walkTarget as THREE.Vector3;
          walkTarget.set(
            nextX,
            0,
            nextZ,
          );
        });
      }
    };
    const hitRobot = (event: PointerEvent) => {
      updatePointer(event);
      raycaster.setFromCamera(pointerTarget, camera);
      const hit = raycaster.intersectObjects(robotMeshes, false)[0];
      return (hit?.object.userData.robotId as RobotId | undefined) ?? null;
    };
    const onMove = (event: PointerEvent) => {
      const id = hitRobot(event);
      if (id !== hovered) {
        if (hovered && robotRoots[hovered]) robotRoots[hovered]!.userData.hovered = false;
        hovered = id;
        if (hovered && robotRoots[hovered]) robotRoots[hovered]!.userData.hovered = true;
        renderer.domElement.style.cursor = id ? 'pointer' : 'grab';
      }
    };
    const onClick = (event: PointerEvent) => {
      const id = hitRobot(event);
      if (!id) return;
      const robot = robotRoots[id];
      if (robot) {
        const celebrationStartedAt = performance.now();
        const previousAction = robot.userData.lastCelebrationAction as RobotAction | undefined;
        const availableActions = ROBOT_ACTIONS.filter((action) => action !== previousAction);
        const action = availableActions[Math.floor(Math.random() * availableActions.length)];
        const previousExpression = robot.userData.lastCelebrationExpression as RobotExpression | undefined;
        const availableExpressions = ROBOT_EXPRESSIONS.filter((expression) => expression !== previousExpression);
        const expression = availableExpressions[Math.floor(Math.random() * availableExpressions.length)];
        robot.userData.celebrationAction = action;
        robot.userData.lastCelebrationAction = action;
        robot.userData.celebrationExpression = expression;
        robot.userData.lastCelebrationExpression = expression;
        robot.userData.winkSide = Math.random() < 0.5 ? 'left' : 'right';
        robot.userData.celebrateStartedAt = celebrationStartedAt;
        robot.userData.celebrateUntil = celebrationStartedAt + 1800;
        callbackRef.current(id, action, expression);
      }
    };
    const onPointerLeave = () => {
      pointerInside = false;
      if (hovered && robotRoots[hovered]) robotRoots[hovered]!.userData.hovered = false;
      hovered = null;
      renderer.domElement.style.cursor = 'grab';
      (Object.values(robotRoots) as THREE.Group[]).forEach((robot) => {
        (robot.userData.walkTarget as THREE.Vector3).set(
          robot.userData.homeX as number,
          0,
          robot.userData.homeZ as number,
        );
      });
    };
    renderer.domElement.addEventListener('pointermove', onMove);
    renderer.domElement.addEventListener('pointerdown', onClick);
    renderer.domElement.addEventListener('pointerleave', onPointerLeave);

    const resize = () => {
      const width = mount.clientWidth;
      const height = mount.clientHeight;
      const isMobile = width < 768;
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, isMobile ? 1.35 : 1.8));
      renderer.setSize(width, height, false);
      const aspect = width / Math.max(height, 1);
      // Standard desktop aspect ratios share one composition. Only genuinely
      // ultrawide or very short windows receive a small framing adjustment.
      const ultraWidePressure = isMobile
        ? 0
        : THREE.MathUtils.clamp((aspect - 2.15) / 0.55, 0, 1);
      const shortScreenPressure = isMobile
        ? 0
        : THREE.MathUtils.clamp((640 - height) / 160, 0, 1);
      const framingPressure = Math.max(ultraWidePressure, shortScreenPressure);
      cameraHome.x = isMobile ? 0.5 : 0.35;
      cameraHome.y = isMobile ? 2.7 : 2.62 + framingPressure * 0.02;
      cameraHome.z = isMobile ? 10.35 : 9.9 + framingPressure * 0.16;
      cameraTarget.x = isMobile ? -0.65 : 0.25;
      cameraTarget.y = isMobile ? 0.1 : 0.48 - framingPressure * 0.28;
      cameraTarget.z = -0.7;
      camera.fov = isMobile ? 43 : 39;
      camera.zoom = isMobile ? 1 : 2 - framingPressure * 0.03;
      camera.aspect = aspect;
      camera.updateProjectionMatrix();
    };
    const observer = new ResizeObserver(resize);
    observer.observe(mount);
    window.addEventListener('resize', resize);
    resize();

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const animate = (now: number) => {
      if (disposed) return;
      const delta = Math.min((now - lastTime) / 1000, 0.05);
      lastTime = now;
      pointer.lerp(pointerTarget, 0.045);

      if (!reducedMotion) {
        camera.position.x += (cameraHome.x + pointer.x * 0.22 - camera.position.x) * Math.min(delta * 1.8, 1);
        camera.position.y += (cameraHome.y + pointer.y * 0.1 - camera.position.y) * Math.min(delta * 1.8, 1);
        camera.lookAt(cameraTarget.x + pointer.x * 0.12, cameraTarget.y + pointer.y * 0.06, cameraTarget.z);
        dust.rotation.y = now * 0.000035;
        dust.position.y = Math.sin(now * 0.00032) * 0.08;
        steam.position.y = 0.82 + Math.sin(now * 0.0015) * 0.025;
        steam.material.opacity = 0.13 + Math.sin(now * 0.0011) * 0.04;
      } else {
        camera.lookAt(cameraTarget);
      }

      (Object.entries(robotRoots) as [RobotId, THREE.Group][]).forEach(([id, robot]) => {
        const phase = robot.userData.phase as number;
        const celebrating = (robot.userData.celebrateUntil ?? 0) > now;
        const celebrationAction = (robot.userData.celebrationAction as RobotAction | undefined) ?? 'bounce';
        const celebrationExpression = (robot.userData.celebrationExpression as RobotExpression | undefined) ?? 'happy';
        const celebrationStartedAt = robot.userData.celebrateStartedAt as number | undefined;
        const celebrationAge = celebrating && celebrationStartedAt !== undefined
          ? Math.max(0, now - celebrationStartedAt)
          : 0;
        const celebrationProgress = celebrating
          ? THREE.MathUtils.clamp(celebrationAge / 1800, 0, 1)
          : 0;
        const actionEnvelope = Math.sin(celebrationProgress * Math.PI);
        const isHovered = robot.userData.hovered as boolean;
        const actionPivot = robot.userData.actionPivot as THREE.Group;
        const headPivot = robot.userData.headPivot as THREE.Group;
        const eyes = robot.userData.eyes as THREE.Object3D[];
        const eyeMaterials = robot.userData.eyeMaterials as THREE.MeshStandardMaterial[];
        const blinkCycle = (now * 0.001 + phase * 0.37) % (id === 'sunny' ? 4.1 : 4.8);
        const blink = blinkCycle < 0.16
          ? Math.max(0.08, Math.abs(blinkCycle - 0.08) / 0.08)
          : 1;
        const walkTarget = robot.userData.walkTarget as THREE.Vector3;
        const velocity = robot.userData.velocity as THREE.Vector3;
        let motionSpeed = velocity.length();

        if (!reducedMotion) {
          const previousX = robot.position.x;
          const previousZ = robot.position.z;
          const offsetX = walkTarget.x - robot.position.x;
          const offsetZ = walkTarget.z - robot.position.z;
          const distance = Math.hypot(offsetX, offsetZ);
          const maxSpeed = celebrating ? 1.4 : 2.2;
          // Hovering is an intentional pause: brake in place before greeting
          // the visitor instead of continuing to chase the pointer underneath it.
          const desiredSpeed = isHovered || distance < 0.035
            ? 0
            : Math.min(maxSpeed, distance * 2.35);
          const inverseDistance = distance > 0.0001 ? 1 / distance : 0;
          const desiredVelocityX = offsetX * inverseDistance * desiredSpeed;
          const desiredVelocityZ = offsetZ * inverseDistance * desiredSpeed;
          const acceleration = 1 - Math.exp(-5.6 * delta);
          velocity.x += (desiredVelocityX - velocity.x) * acceleration;
          velocity.z += (desiredVelocityZ - velocity.z) * acceleration;
          if (distance < 0.035 && velocity.lengthSq() < 0.002) velocity.set(0, 0, 0);

          robot.position.x += velocity.x * delta;
          robot.position.z += velocity.z * delta;
          const travelled = Math.hypot(robot.position.x - previousX, robot.position.z - previousZ);
          motionSpeed = velocity.length();
          const moving = motionSpeed > 0.035;
          const wheels = robot.userData.wheels as THREE.Object3D[];
          wheels.forEach((wheel) => {
            wheel.rotation.x -= travelled * 8.5;
          });
          const marker = robot.userData.followMarker as THREE.Mesh;
          const markerMaterial = robot.userData.followMarkerMaterial as THREE.MeshBasicMaterial;
          marker.position.x += (walkTarget.x - marker.position.x) * Math.min(delta * 14, 1);
          marker.position.z += (walkTarget.z - marker.position.z) * Math.min(delta * 14, 1);
          const markerOpacity = pointerInside && distance > 0.12 ? 0.34 : 0;
          markerMaterial.opacity += (markerOpacity - markerMaterial.opacity) * Math.min(delta * 8, 1);
          const markerPulse = 1 + Math.sin(now * 0.007) * 0.12;
          marker.scale.setScalar(markerPulse);
          const rimTarget = robot.userData.rimTarget as THREE.Object3D;
          rimTarget.position.x += (robot.position.x - rimTarget.position.x) * Math.min(delta * 4, 1);
          rimTarget.position.z += (robot.position.z - rimTarget.position.z) * Math.min(delta * 4, 1);

          // Locomotion stays grounded. Click reactions live on a nested pivot so
          // spins, wiggles and bows never corrupt the robot's travel heading.
          const maxJumpHeight = Math.min(0.075, (robot.userData.targetHeight as number) * 0.11);
          let jumpHeight = 0;
          let actionOffsetX = 0;
          let actionYaw = 0;
          let actionPitch = 0;
          let actionRoll = 0;
          if (celebrating) {
            if (celebrationAction === 'bounce') {
              jumpHeight = Math.abs(Math.sin(celebrationAge * 0.012)) * maxJumpHeight;
              actionRoll = Math.sin(celebrationAge * 0.018) * 0.045;
            } else if (celebrationAction === 'spin') {
              const spinProgress = THREE.MathUtils.clamp(celebrationAge / 1050, 0, 1);
              const easedSpin = 1 - Math.pow(1 - spinProgress, 3);
              actionYaw = easedSpin * Math.PI * 2;
              jumpHeight = Math.abs(Math.sin(celebrationAge * 0.014)) * maxJumpHeight * 0.38;
              actionRoll = Math.sin(celebrationAge * 0.02) * 0.05 * (1 - spinProgress);
            } else if (celebrationAction === 'wiggle') {
              actionOffsetX = Math.sin(celebrationAge * 0.022) * 0.035 * actionEnvelope;
              actionYaw = Math.sin(celebrationAge * 0.018) * 0.28 * actionEnvelope;
              actionRoll = Math.sin(celebrationAge * 0.024) * 0.16 * actionEnvelope;
              jumpHeight = Math.abs(Math.sin(celebrationAge * 0.022)) * maxJumpHeight * 0.18 * actionEnvelope;
            } else {
              actionPitch = Math.abs(Math.sin(celebrationAge * 0.014)) * 0.16 * actionEnvelope;
              jumpHeight = Math.abs(Math.sin(celebrationAge * 0.014)) * maxJumpHeight * 0.08;
            }
          }
          actionPivot.position.set(actionOffsetX, jumpHeight, 0);
          actionPivot.rotation.set(actionPitch, actionYaw, actionRoll);
          robot.position.y = robot.userData.baseY as number;
          if (isHovered || moving || distance > 0.08) {
            const desiredHeading = isHovered
              ? Math.atan2(camera.position.x - robot.position.x, camera.position.z - robot.position.z)
              : Math.atan2(offsetX, offsetZ);
            const headingDelta = Math.atan2(
              Math.sin(desiredHeading - robot.rotation.y),
              Math.cos(desiredHeading - robot.rotation.y),
            );
            robot.rotation.y += headingDelta * (1 - Math.exp(-(isHovered ? 9.5 : 7.5) * delta));
          }
          const leanSmoothing = 1 - Math.exp(-8 * delta);
          const targetRoll = THREE.MathUtils.clamp(-velocity.x * 0.018, -0.022, 0.022);
          const targetPitch = THREE.MathUtils.clamp(velocity.z * 0.012, -0.016, 0.016);
          robot.rotation.z += (targetRoll - robot.rotation.z) * leanSmoothing;
          robot.rotation.x += (targetPitch - robot.rotation.x) * leanSmoothing;
        }

        // The head anticipates the destination, then settles in line with the body.
        const idleYaw = reducedMotion ? 0 : Math.sin(now * 0.00115 + phase) * 0.12;
        const idlePitch = reducedMotion ? 0 : Math.sin(now * 0.00145 + phase) * 0.04;
        const idleRoll = reducedMotion ? 0 : Math.sin(now * 0.0009 + phase) * 0.02;
        const targetDistance = Math.hypot(walkTarget.x - robot.position.x, walkTarget.z - robot.position.z);
        const targetWorldYaw = isHovered
          ? Math.atan2(camera.position.x - robot.position.x, camera.position.z - robot.position.z)
          : targetDistance > 0.04
          ? Math.atan2(walkTarget.x - robot.position.x, walkTarget.z - robot.position.z)
          : robot.rotation.y;
        const localLookYaw = Math.atan2(
          Math.sin(targetWorldYaw - robot.rotation.y),
          Math.cos(targetWorldYaw - robot.rotation.y),
        );
        let headYaw = THREE.MathUtils.clamp(
          localLookYaw * (isHovered ? 1.05 : 0.82) + pointer.x * 0.1 + idleYaw,
          -0.78,
          0.78,
        );
        let headPitch = isHovered ? -0.08 : -pointer.y * 0.2 + idlePitch;
        let headRoll = idleRoll;
        if (celebrating && !reducedMotion) {
          if (celebrationAction === 'bounce') {
            headYaw = Math.sin(celebrationAge * 0.014) * 0.52;
            headPitch = -0.12 + Math.sin(celebrationAge * 0.011) * 0.07;
            headRoll = Math.sin(celebrationAge * 0.013) * 0.15;
          } else if (celebrationAction === 'spin') {
            headYaw = Math.sin(celebrationAge * 0.009) * 0.18;
            headPitch = -0.09;
            headRoll = Math.sin(celebrationAge * 0.012) * 0.08;
          } else if (celebrationAction === 'wiggle') {
            headYaw = Math.sin(celebrationAge * 0.018) * 0.48 * actionEnvelope;
            headPitch = -0.07 + Math.sin(celebrationAge * 0.021) * 0.06;
            headRoll = -Math.sin(celebrationAge * 0.024) * 0.14 * actionEnvelope;
          } else {
            headYaw = 0;
            headPitch = -0.06 + Math.abs(Math.sin(celebrationAge * 0.014)) * 0.3 * actionEnvelope;
            headRoll = 0;
          }
        }
        headPivot.rotation.y += (headYaw - headPivot.rotation.y) * Math.min(delta * 7.5, 1);
        headPivot.rotation.x += (headPitch - headPivot.rotation.x) * Math.min(delta * 7, 1);
        headPivot.rotation.z += (headRoll - headPivot.rotation.z) * Math.min(delta * 8, 1);

        eyes.forEach((eye, index) => {
          const baseScale = eye.userData.baseScale as THREE.Vector3;
          const eyeSide = (eye.userData.eyeSide as 'left' | 'right' | undefined)
            ?? (index === 0 ? 'left' : 'right');
          const accentSide = (robot.userData.winkSide as 'left' | 'right' | undefined) ?? 'left';
          let expressionScaleY = blink * (isHovered ? 1.12 : 1);
          let expressionScaleX = isHovered ? 1.08 : 1;
          let expressionTilt = 0;
          if (celebrating) {
            if (celebrationExpression === 'happy') {
              expressionScaleY = 0.3;
              expressionScaleX = 1.04;
              expressionTilt = eyeSide === 'left' ? -0.25 : 0.25;
            } else if (celebrationExpression === 'surprised') {
              const surprisePulse = 1 + Math.sin(celebrationAge * 0.022) * 0.055;
              expressionScaleY = 1.24 * surprisePulse;
              expressionScaleX = 1.15 * surprisePulse;
              expressionTilt = eyeSide === 'left' ? -0.045 : 0.045;
            } else if (celebrationExpression === 'wink') {
              const isWinkingEye = eyeSide === accentSide;
              expressionScaleY = isWinkingEye
                ? 0.1 + Math.abs(Math.sin(celebrationAge * 0.018)) * 0.07
                : 1.04;
              expressionScaleX = isWinkingEye ? 1.1 : 1.02;
              expressionTilt = isWinkingEye
                ? (eyeSide === 'left' ? -0.18 : 0.18)
                : (eyeSide === 'left' ? 0.035 : -0.035);
            } else {
              const isCuriousEye = eyeSide === accentSide;
              expressionScaleY = isCuriousEye ? 1.2 : 0.72;
              expressionScaleX = isCuriousEye ? 1.13 : 0.94;
              expressionTilt = eyeSide === 'left' ? -0.1 : 0.1;
            }
          }
          const targetScaleY = baseScale.y * expressionScaleY;
          eye.scale.y += (targetScaleY - eye.scale.y) * Math.min(delta * 24, 1);
          const targetRotationZ = (eye.userData.baseRotationZ as number) + expressionTilt;
          eye.rotation.z += (targetRotationZ - eye.rotation.z) * Math.min(delta * 12, 1);
          const targetScaleX = baseScale.x * expressionScaleX;
          eye.scale.x += (targetScaleX - eye.scale.x) * Math.min(delta * 10, 1);
          // Keep eyes locked to the head pivot so they don't drift out of the back-light frame.
          eye.rotation.x = 0;
          eye.rotation.y = 0;
        });
        eyeMaterials.forEach((eyeMaterial) => {
          const baseIntensity = eyeMaterial.userData.baseEmissiveIntensity as number;
          const expressionGlow = celebrationExpression === 'surprised'
            ? 2.25
            : celebrationExpression === 'wink'
              ? 1.55
              : celebrationExpression === 'curious'
                ? 1.72
                : 1.8;
          eyeMaterial.emissiveIntensity = celebrating
            ? baseIntensity * (expressionGlow + Math.sin(now * 0.02) * 0.14)
            : isHovered ? baseIntensity * 1.35 : baseIntensity * (1 + Math.sin(now * 0.003 + phase) * 0.08);
        });
      });

      const speechBubble = speechBubbleRef.current;
      if (speechBubble) {
        const speechRobotId = speechBubble.dataset.robotId as RobotId | undefined;
        const speechRobot = speechRobotId ? robotRoots[speechRobotId] : undefined;
        if (speechRobot) {
          const actionPivot = speechRobot.userData.actionPivot as THREE.Group;
          const targetHeight = speechRobot.userData.targetHeight as number;
          speechRobot.updateMatrixWorld(true);
          camera.updateMatrixWorld();
          speechAnchor.set(0, targetHeight + 0.055, 0);
          actionPivot.localToWorld(speechAnchor);
          speechAnchor.project(camera);
          const isVisible = speechAnchor.z > -1 && speechAnchor.z < 1;
          // Project against the rendered canvas rectangle, then translate into
          // section-local coordinates for the HTML speech bubble.
          const canvasRect = renderer.domElement.getBoundingClientRect();
          const mountRect = mount.getBoundingClientRect();
          const width = mount.clientWidth;
          const height = mount.clientHeight;
          const unclampedX = (speechAnchor.x * 0.5 + 0.5) * canvasRect.width
            + canvasRect.left - mountRect.left;
          const unclampedY = (-speechAnchor.y * 0.5 + 0.5) * canvasRect.height
            + canvasRect.top - mountRect.top;
          const horizontalMargin = Math.min(128, width * 0.28);
          const bubbleX = THREE.MathUtils.clamp(unclampedX, horizontalMargin, width - horizontalMargin);
          const bubbleY = THREE.MathUtils.clamp(unclampedY, 92, height - 48);
          speechBubble.style.visibility = isVisible ? 'visible' : 'hidden';
          speechBubble.style.transform = `translate3d(${bubbleX}px, ${bubbleY}px, 0) translate(-50%, -100%)`;
        } else {
          speechBubble.style.visibility = 'hidden';
        }
      }

      renderer.render(scene, camera);
      frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);

    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener('resize', resize);
      renderer.domElement.removeEventListener('pointermove', onMove);
      renderer.domElement.removeEventListener('pointerdown', onClick);
      renderer.domElement.removeEventListener('pointerleave', onPointerLeave);
      renderer.dispose();
      dracoLoader.dispose();
      scene.traverse((object) => {
        const mesh = object as THREE.Mesh;
        mesh.geometry?.dispose();
        const materials = Array.isArray(mesh.material) ? mesh.material : mesh.material ? [mesh.material] : [];
        materials.forEach((item) => item.dispose());
      });
      mount.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={mountRef} className="absolute inset-0" />;
}

export const HeroSlider: React.FC = () => {
  const { i18n } = useTranslation();
  const [activeInteraction, setActiveInteraction] = useState<RobotInteraction | null>(null);
  const speechBubbleRef = useRef<HTMLDivElement>(null);
  const speechTimerRef = useRef<number | null>(null);
  const interactionSequenceRef = useRef(0);
  const isChinese = i18n.language.startsWith('zh');
  const isJapanese = i18n.language.startsWith('ja');
  const copy = isChinese
    ? { titleA: '每一天，都有', titleB: '歪觅在身边', body: '它会听、会说、会看，也会跳舞——让科技自然融入家庭。', explore: '认识歪觅', hint: '移动鼠标探索 · 点击歪觅打招呼' }
    : isJapanese
      ? { titleA: '毎日のそばに、', titleB: 'WYNDME がいる。', body: '聴いて、話して、見て、踊る。テクノロジーを、暮らしに自然になじむ存在へ。', explore: 'WYNDME に会う', hint: 'マウスで探索 · WYNDME をクリック' }
      : { titleA: 'Every day feels', titleB: 'better together.', body: 'It listens, talks, sees, and dances—making technology feel naturally at home.', explore: 'Meet WYNDME', hint: 'Move to explore · Click WYNDME to say hello' };

  const speechCopy: Record<RobotAction, string> = isChinese
    ? {
        bounce: '嘿！看到你啦！',
        spin: '转个圈给你看！',
        wiggle: '今天也要开心呀～',
        bow: '嗯嗯，我在认真听！',
      }
    : isJapanese
      ? {
          bounce: 'やあ！見つけたよ！',
          spin: 'くるっと回るね！',
          wiggle: '今日も楽しくいこう！',
          bow: 'うんうん、聞いているよ！',
        }
      : {
          bounce: 'Hey! I see you!',
          spin: 'Watch me spin!',
          wiggle: 'Let’s have some fun!',
          bow: 'Mm-hmm, I’m listening!',
        };

  useEffect(() => () => {
    if (speechTimerRef.current !== null) window.clearTimeout(speechTimerRef.current);
  }, []);

  const selectRobot = (id: RobotId, action: RobotAction, expression: RobotExpression) => {
    interactionSequenceRef.current += 1;
    const sequence = interactionSequenceRef.current;
    setActiveInteraction({ id, action, expression, sequence });
    if (speechTimerRef.current !== null) window.clearTimeout(speechTimerRef.current);
    speechTimerRef.current = window.setTimeout(() => {
      setActiveInteraction((current) => (current?.sequence === sequence ? null : current));
    }, 2400);
  };

  return (
    <section className="relative h-[100svh] min-h-[620px] overflow-hidden bg-[#171411] text-white md:min-h-0" aria-label="WYNDME 互动产品体验">
      <HomeScene onRobotSelect={selectRobot} speechBubbleRef={speechBubbleRef} />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(18,15,12,.94)_0%,rgba(18,15,12,.7)_28%,rgba(18,15,12,.06)_61%,rgba(18,15,12,.18)_100%)] max-md:bg-[linear-gradient(180deg,rgba(18,15,12,.62)_0%,rgba(18,15,12,.04)_36%,rgba(18,15,12,.24)_58%,rgba(18,15,12,.94)_82%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_69%_45%,transparent_0%,rgba(24,18,13,.08)_52%,rgba(6,5,4,.62)_100%)]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/45 to-transparent" />

      <div className="hero-copy-shell pointer-events-none relative z-10 mx-auto flex h-full w-full max-w-[1600px] items-end px-6 pb-24 pt-28 md:items-center md:px-12 md:pb-0 lg:px-20">
        <div className="max-w-[570px] md:pb-0">
          <h1 className="font-sans text-[clamp(2.65rem,5.3vw,5.8rem)] font-semibold leading-[0.98] tracking-[-0.055em] drop-shadow-2xl">
            <span className="block text-white">{copy.titleA}</span>
            <span className="mt-1 block text-[#f3cc62]">{copy.titleB}</span>
          </h1>
          <p className="mt-5 max-w-md text-sm leading-6 text-white/72 sm:mt-7 sm:text-lg sm:leading-8">{copy.body}</p>
          <div className="hero-actions pointer-events-auto mt-7 flex flex-wrap items-center gap-4 sm:mt-9">
            <button onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })} className="group flex items-center gap-3 rounded-full bg-[#f0c85a] px-6 py-3.5 text-sm font-bold text-[#17130a] shadow-[0_12px_38px_rgba(240,200,90,.2)] transition hover:-translate-y-0.5 hover:bg-[#ffdb73] focus:outline-none focus:ring-2 focus:ring-white">
              {copy.explore}<ArrowDown size={17} className="transition-transform group-hover:translate-y-0.5" />
            </button>
          </div>
        </div>
      </div>

      <div className="hero-hint pointer-events-none absolute bottom-9 right-6 z-20 hidden items-center gap-2 text-xs tracking-wide text-white/60 md:flex lg:right-12">
        <MousePointer2 size={15} className="text-[#f0c85a]" />
        {copy.hint}
      </div>

      {activeInteraction && (
        <div
          key={activeInteraction.sequence}
          ref={speechBubbleRef}
          data-robot-id={activeInteraction.id}
          data-expression={activeInteraction.expression}
          className="pointer-events-none invisible absolute left-0 top-0 z-30 will-change-transform"
          role="status"
          aria-live="polite"
        >
          <div className="robot-speech-card">
            <div className="flex items-center justify-center gap-1.5 text-[10px] font-black uppercase tracking-[0.12em]" style={{ color: robotCopy[activeInteraction.id].color }}>
              <Sparkles size={13} strokeWidth={2.8} />{robotCopy[activeInteraction.id].name}
            </div>
            <p className="mt-1 whitespace-nowrap text-[15px] font-extrabold leading-5 text-[#19140e]">
              {speechCopy[activeInteraction.action]}
            </p>
          </div>
        </div>
      )}
    </section>
  );
};
